/**
 * Hook that manages the tool call side panel visibility and auto-expand behavior.
 *
 * Automatically opens the panel when new (non-interactive) tool calls complete,
 * respects user intent when they manually close it, and suppresses auto-open
 * for pre-existing tool calls on page load.
 *
 * @module hooks/use-tool-panel
 */
import { useEffect, useMemo, useState } from "react";
import { toolNameOfPart } from "@/components/chat/messages/tool-call-message/tool-part";
import { useChatConfig } from "@/hooks/use-chat-config";
import type { ChatMessage } from "@/types/chat-message";

/** Interactive presentation tools that render inline — no side panel needed */
const INTERACTIVE_TOOL_IDS = new Set([
  "render_ui",
]);

/**
 * Counts completed non-interactive tool calls across all messages.
 * Used to detect when new tool results arrive.
 */
function countToolCalls(messages: ChatMessage[]): number {
  return messages.reduce((count, message) => {
    const toolCalls =
      message?.parts?.filter((part: Record<string, string>) => {
        if (part.state !== "output-available") return false;
        const name = toolNameOfPart(part as { type: string; toolName?: string });
        if (name === undefined) return false;
        return !INTERACTIVE_TOOL_IDS.has(name);
      }) || [];
    return count + toolCalls.length;
  }, 0);
}

interface UseToolPanelOptions {
  /** Current messages array from useChat */
  messages: ChatMessage[];
  /** Initial messages loaded with the chat (for baseline tool call count) */
  initialMessages: ChatMessage[];
  /** Chat streaming status */
  status: string;
  /** Whether the chat is in compact/widget mode (suppresses auto-expand) */
  isCompact: boolean;
}

/**
 * Manages tool panel visibility with auto-expand on new tool calls.
 *
 * @returns Panel state and callbacks to wire into MultimodalInput and ToolCallSidePanel
 */
export function useToolPanel({
  messages,
  initialMessages,
  status,
  isCompact,
}: UseToolPanelOptions) {
  const { isChatToolVisible, setChatToolVisible } = useChatConfig();

  /**
   * Track if user has manually closed the panel.
   * Initialized to true so existing tool calls don't auto-expand on page load.
   */
  const [hasUserClosedPanel, setHasUserClosedPanel] = useState(true);

  /**
   * Baseline tool call count from initial messages.
   * Prevents pre-existing tool calls from being treated as "new".
   */
  const initialToolCallCount = useMemo(
    () => countToolCalls(initialMessages),
    [initialMessages],
  );

  const [lastToolCallCount, setLastToolCallCount] =
    useState(initialToolCallCount);

  /**
   * Auto-expand the panel when new non-interactive tool calls complete.
   * Respects the user's manual close — won't reopen for the same batch.
   */
  useEffect(() => {
    if (status !== "ready" || messages.length === 0) return;

    const currentToolCallCount = countToolCalls(messages);
    const hasNewToolCalls = currentToolCallCount > lastToolCallCount;

    if (hasNewToolCalls) {
      setHasUserClosedPanel(false);
      setLastToolCallCount(currentToolCallCount);

      if (!isChatToolVisible && !isCompact) {
        setChatToolVisible(true);
      }
    } else if (
      currentToolCallCount > 0 &&
      !isChatToolVisible &&
      !hasUserClosedPanel &&
      !isCompact
    ) {
      setChatToolVisible(true);
    }
  }, [
    messages,
    status,
    isChatToolVisible,
    hasUserClosedPanel,
    lastToolCallCount,
    setChatToolVisible,
    isCompact,
  ]);

  /** Call when user explicitly opens the tool panel (e.g. from input button) */
  const onToolPanelOpen = () => setHasUserClosedPanel(false);

  /** Call when user explicitly closes the tool panel */
  const onToolPanelClose = () => {
    setChatToolVisible(false);
    setHasUserClosedPanel(true);
  };

  return {
    isChatToolVisible,
    onToolPanelOpen,
    onToolPanelClose,
  };
}
